import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, ColorType, LineStyle, CandlestickSeries, LineSeries, HistogramSeries, CrosshairMode } from 'lightweight-charts';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp, RefreshCw, Maximize2, Minimize2, WifiOff } from "lucide-react";
import { getPricePrecision } from './DcaUtils';
import { getChartAdapter, Timeframe } from './ChartAdapters';


interface PositionDetail {
  side: string;
  avg_price: number;
  tp_price?: number;
  sl_price?: number;
}

interface TradingChartProps {
  symbol: string;
  exchange?: string;
  isTestnet?: boolean;
  positions?: PositionDetail[];
  theme?: string;
}

const TIMEFRAMES: { label: string; value: Timeframe }[] = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' },
];

export const TradingChart = React.memo<TradingChartProps>(({ symbol, exchange, isTestnet, positions, theme }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candlestickSeriesRef = useRef<any>(null);
  const priceLinesRef = useRef<any[]>([]);
  const ema7SeriesRef = useRef<any>(null);
  const ema25SeriesRef = useRef<any>(null);
  const ema60SeriesRef = useRef<any>(null);
  const ema120SeriesRef = useRef<any>(null);
  const ema240SeriesRef = useRef<any>(null);
  const volumeSeriesRef = useRef<any>(null);
  const rsiSeriesRef = useRef<any>(null);
  const bbUpperSeriesRef = useRef<any>(null);
  const bbMiddleSeriesRef = useRef<any>(null);
  const bbLowerSeriesRef = useRef<any>(null);
  const adxDataRef = useRef<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [timeframe, setTimeframe] = useState<Timeframe>('15m');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [useTestnetData, setUseTestnetData] = useState(false);

  const [visibleEmas, setVisibleEmas] = useState<Record<number, boolean>>({
    7: true,
    25: true,
    60: true,
    120: true,
    240: true
  });
  const [visiblePositions, setVisiblePositions] = useState<Record<string, boolean>>({
    long: true,
    short: true
  });
  const [visibleVolume, setVisibleVolume] = useState(false);
  const [visibleRsi, setVisibleRsi] = useState(false);
  const [visibleBb, setVisibleBb] = useState(false);

  // Bollinger Bands 계산 함수
  const calculateBB = (data: any[], period: number = 20, multiplier: number = 2) => {
    if (data.length < period) return { upper: [], middle: [], lower: [] };
    const upper = [];
    const middle = [];
    const lower = [];

    for (let i = period - 1; i < data.length; i++) {
      const slice = data.slice(i - period + 1, i + 1);
      const sum = slice.reduce((acc, d) => acc + d.close, 0);
      const avg = sum / period;
      const variance = slice.reduce((acc, d) => acc + Math.pow(d.close - avg, 2), 0) / period;
      const stdDev = Math.sqrt(variance);

      middle.push({ time: data[i].time, value: avg });
      upper.push({ time: data[i].time, value: avg + multiplier * stdDev });
      lower.push({ time: data[i].time, value: avg - multiplier * stdDev });
    }
    return { upper, middle, lower };
  };

  // ADX 계산 함수 (기본 기간 7로 단축하여 백엔드와 동기화)
  const calculateADX = (data: any[], period: number = 7) => {
    if (data.length < period * 2) return [];

    const adxData = [];
    const tr = [];
    const plusDM = [];
    const minusDM = [];

    for (let i = 1; i < data.length; i++) {
      const high = data[i].high;
      const low = data[i].low;
      const prevHigh = data[i - 1].high;
      const prevLow = data[i - 1].low;
      const prevClose = data[i - 1].close;

      const trVal = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
      tr.push(trVal);

      const upMove = high - prevHigh;
      const downMove = prevLow - low;

      if (upMove > downMove && upMove > 0) {
        plusDM.push(upMove);
      } else {
        plusDM.push(0);
      }

      if (downMove > upMove && downMove > 0) {
        minusDM.push(downMove);
      } else {
        minusDM.push(0);
      }
    }

    // Smoothed values
    let smoothedTR = tr.slice(0, period).reduce((a, b) => a + b, 0);
    let smoothedPlusDM = plusDM.slice(0, period).reduce((a, b) => a + b, 0);
    let smoothedMinusDM = minusDM.slice(0, period).reduce((a, b) => a + b, 0);

    const dxValues = [];
    for (let i = period - 1; i < tr.length; i++) {
      if (i > period - 1) {
        smoothedTR = smoothedTR - smoothedTR / period + tr[i];
        smoothedPlusDM = smoothedPlusDM - smoothedPlusDM / period + plusDM[i];
        smoothedMinusDM = smoothedMinusDM - smoothedMinusDM / period + minusDM[i];
      }

      const plusDI = (smoothedPlusDM / smoothedTR) * 100;
      const minusDI = (smoothedMinusDM / smoothedTR) * 100;
      const dx = Math.abs(plusDI - minusDI) / (plusDI + minusDI) * 100;
      dxValues.push({ time: data[i + 1].time, value: dx });
    }

    // ADX Smoothing
    let adxSum = dxValues.slice(0, period).reduce((a, b) => a + b.value, 0);
    let adx = adxSum / period;
    adxData.push({ time: dxValues[period - 1].time, value: adx });

    for (let i = period; i < dxValues.length; i++) {
      adx = (adx * (period - 1) + dxValues[i].value) / period;
      adxData.push({ time: dxValues[i].time, value: adx });
    }

    return adxData;
  };

  // RSI 계산 함수
  const calculateRSI = (data: any[], period: number = 14) => {
    if (data.length <= period) return [];
    const rsiData = [];
    let gains = 0;
    let losses = 0;

    for (let i = 1; i <= period; i++) {
      const diff = data[i].close - data[i - 1].close;
      if (diff >= 0) gains += diff;
      else losses -= diff;
    }

    let avgGain = gains / period;
    let avgLoss = losses / period;

    for (let i = period; i < data.length; i++) {
      if (i > period) {
        const diff = data[i].close - data[i - 1].close;
        const currentGain = diff >= 0 ? diff : 0;
        const currentLoss = diff < 0 ? -diff : 0;
        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
      }
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - (100 / (1 + rs));
      rsiData.push({ time: data[i].time, value: rsi });
    }
    return rsiData;
  };

  // EMA 계산 함수 (메모이제이션 고려 가능하지만 내부 로직이므로 일단 유지)
  const calculateEMA = (data: any[], period: number) => {
    const k = 2 / (period + 1);
    let emaData = [];
    if (data.length === 0) return [];
    let prevEma = data[0].close;

    for (let i = 0; i < data.length; i++) {
      const emaValue = data[i].close * k + prevEma * (1 - k);
      emaData.push({
        time: data[i].time,
        value: emaValue,
      });
      prevEma = emaValue;
    }
    return emaData;
  };

  const fetchData = useCallback(async (isInitial = false) => {
    if (!symbol) return;
    if (!navigator.onLine) {
      setIsOnline(false);
      return;
    }
    if (isInitial) setIsInitialLoading(true);
    setIsLoading(true);

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const adapter = getChartAdapter(exchange);
      const formattedData = await adapter.fetchData(symbol, timeframe, useTestnetData, controller.signal);

      if (controller.signal.aborted) return;
      clearTimeout(timeoutId);

      if (candlestickSeriesRef.current && formattedData.length > 0) {
        candlestickSeriesRef.current.setData(formattedData);

        // 첫 로딩 시 차트 범위를 최신 데이터(오른쪽 끝)로 맞춤
        if (isInitial && chartRef.current) {
          chartRef.current.timeScale().fitContent();
        }

        // EMA 데이터 설정
        if (ema7SeriesRef.current) ema7SeriesRef.current.setData(calculateEMA(formattedData, 7));
        if (ema25SeriesRef.current) ema25SeriesRef.current.setData(calculateEMA(formattedData, 25));
        if (ema60SeriesRef.current) ema60SeriesRef.current.setData(calculateEMA(formattedData, 60));
        if (ema120SeriesRef.current) ema120SeriesRef.current.setData(calculateEMA(formattedData, 120));
        if (ema240SeriesRef.current) ema240SeriesRef.current.setData(calculateEMA(formattedData, 240));

        // Volume 데이터 설정
        if (volumeSeriesRef.current) {
          const volumeData = formattedData.map((d) => ({
            time: d.time,
            value: d.volume,
            color: d.close >= d.open ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
          }));
          volumeSeriesRef.current.setData(volumeData);
        }

        // RSI 데이터 설정
        if (rsiSeriesRef.current) {
          rsiSeriesRef.current.setData(calculateRSI(formattedData, 14));
        }

        // BB 데이터 설정
        if (bbUpperSeriesRef.current && bbMiddleSeriesRef.current && bbLowerSeriesRef.current) {
          const { upper, middle, lower } = calculateBB(formattedData, 20, 2);
          bbUpperSeriesRef.current.setData(upper);
          bbMiddleSeriesRef.current.setData(middle);
          bbLowerSeriesRef.current.setData(lower);
        }

        // ADX 데이터 계산 및 저장 (백엔드와 동일하게 Period 7 적용)
        adxDataRef.current = calculateADX(formattedData, 7);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        // 네트워크 연결 끊김으로 인한 에러는 콘솔 출력을 최소화하고 상태만 업데이트
        if (!navigator.onLine || (error instanceof TypeError && error.message === 'Failed to fetch')) {
          setIsOnline(false);
        } else {
          console.error('Failed to fetch chart data:', error);
        }
      }
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [symbol, timeframe, exchange, useTestnetData]);

  const handleRefresh = useCallback(async () => {
    if (!navigator.onLine) return;
    await fetchData(true);
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, [fetchData]);

  // 네트워크 상태 감지
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      fetchData(false); // 다시 연결되면 데이터 갱신
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchData]);

  // 차트 생성 및 파괴
  useEffect(() => {
    if (isOpen && chartContainerRef.current && !chartRef.current) {
      const isDark = document.documentElement.classList.contains('dark');

      const chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: isDark ? '#D1D4DC' : '#131722',
        },
        grid: {
          vertLines: { color: isDark ? 'rgba(70, 70, 70, 0.2)' : 'rgba(230, 230, 230, 0.5)' },
          horzLines: { color: isDark ? 'rgba(70, 70, 70, 0.2)' : 'rgba(230, 230, 230, 0.5)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: 250,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          tickMarkFormatter: (time: number, tickMarkType: number, locale: string) => {
            const date = new Date(time * 1000);
            const options: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Seoul', hour12: false };

            // 틱 타입에 따른 포맷팅 (0: Year, 1: Month, 2: Day, 3: Hour/Min, 4: Sec)
            switch (tickMarkType) {
              case 0: options.year = 'numeric'; break;
              case 1: options.month = 'short'; break;
              case 2: options.day = 'numeric'; break;
              case 3: options.hour = '2-digit'; options.minute = '2-digit'; break;
              case 4: options.hour = '2-digit'; options.minute = '2-digit'; options.second = '2-digit'; break;
              default: options.hour = '2-digit'; options.minute = '2-digit'; break;
            }

            return new Intl.DateTimeFormat('ko-KR', options).format(date);
          },
        },
        handleScroll: true,
        handleScale: true,
        crosshair: {
          mode: CrosshairMode.Normal,
        },
        localization: {
          timeFormatter: (time: number) => {
            const date = new Date(time * 1000);
            return new Intl.DateTimeFormat('ko-KR', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false,
              timeZone: 'Asia/Seoul'
            }).format(date);
          },
        },
      });

      // 범례(Legend) 요소 생성
      const legend = document.createElement('div');
      legend.style.position = 'absolute';
      legend.style.left = '12px';
      legend.style.top = '12px';
      legend.style.zIndex = '5';
      legend.style.fontSize = '11px';
      legend.style.fontFamily = 'Inter, sans-serif';
      legend.style.fontWeight = '900';
      legend.style.pointerEvents = 'none';
      legend.style.display = 'flex';
      legend.style.gap = '8px';
      legend.style.color = isDark ? '#D1D4DC' : '#131722';
      chartContainerRef.current?.appendChild(legend);

      const precision = getPricePrecision(symbol);
      const minMove = 1 / Math.pow(10, precision);

      const setLegendText = (priceData?: any, cursorPrice?: number, lastPrice?: number, time?: any) => {
        if (!priceData && !cursorPrice) {
          legend.innerHTML = '';
          return;
        }

        const { open, high, low, close } = priceData || {};
        const f = (n: number) => n.toFixed(precision);

        let html = '';

        // ADX 값 찾기 (현재 캔들 시간 기준)
        let adxVal = null;
        if (time && adxDataRef.current.length > 0) {
          const found = adxDataRef.current.find(d => d.time === time);
          if (found) adxVal = found.value;
        } else if (adxDataRef.current.length > 0) {
          // 커서가 없을 때는 마지막 ADX 표시
          adxVal = adxDataRef.current[adxDataRef.current.length - 1].value;
        }

        // 1. 커서 위치 가격 및 현재가 대비 격차 (%)
        if (cursorPrice && lastPrice) {
          const diff = ((cursorPrice - lastPrice) / lastPrice) * 100;
          const diffColor = diff >= 0 ? '#26a69a' : '#ef5350';
          const diffSign = diff >= 0 ? '+' : '';
          html += `
            <div style="display: flex; gap: 6px; align-items: center; margin-right: 12px; border-right: 1px solid rgba(128,128,128,0.2); padding-right: 12px;">
              <span style="color: ${diffColor}">${f(cursorPrice)}</span>
              <span style="font-size: 10px; color: ${diffColor}; background: ${diffColor}15; padding: 1px 4px; border-radius: 4px;">${diffSign}${diff.toFixed(2)}%</span>
            </div>
          `;
        }

        // ADX 표시 (특히 1h 봉에서 강조)
        if (adxVal !== null) {
          const isTrending = adxVal > 22; // 기준값 22
          const adxColor = isTrending ? '#ef5350' : '#22c55e';
          const adxLabel = timeframe === '1h' ? '1h ADX(7)' : 'ADX(7)';
          html += `
            <div style="display: flex; gap: 4px; align-items: center; margin-right: 12px; border-right: 1px solid rgba(128,128,128,0.2); padding-right: 12px;">
              <span style="opacity: 0.5;">${adxLabel}</span>
              <span style="color: ${adxColor}">${adxVal.toFixed(1)}</span>
            </div>
          `;
        }

        // 2. 캔들 OHLC 정보 (데이터가 있을 때만)
        if (priceData) {
          const isUp = close >= open;
          const color = isUp ? '#26a69a' : '#ef5350';
          html += `
            <div style="display: flex; gap: 4px;">
              <span style="opacity: 0.5;">O</span><span style="color: ${color}">${f(open)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">H</span><span style="color: ${color}">${f(high)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">L</span><span style="color: ${color}">${f(low)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">C</span><span style="color: ${color}">${f(close)}</span>
            </div>
          `;
        }

        legend.innerHTML = html;
      };

      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350', borderVisible: false,
        wickUpColor: '#26a69a', wickDownColor: '#ef5350', priceLineVisible: true,
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });

      const ema7Series = chart.addSeries(LineSeries, {
        color: isDark ? '#ffffff' : '#000000', lineWidth: 1,
        crosshairMarkerVisible: false, priceLineVisible: false, visible: visibleEmas[7],
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });
      const ema25Series = chart.addSeries(LineSeries, {
        color: '#f82f2bff', lineWidth: 1,
        crosshairMarkerVisible: false, priceLineVisible: false, visible: visibleEmas[25],
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });
      const ema60Series = chart.addSeries(LineSeries, {
        color: '#fda635ff', lineWidth: 1,
        crosshairMarkerVisible: false, priceLineVisible: false, visible: visibleEmas[60],
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });
      const ema120Series = chart.addSeries(LineSeries, {
        color: '#22c55e', lineWidth: 1,
        crosshairMarkerVisible: false, priceLineVisible: false, visible: visibleEmas[120],
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });
      const ema240Series = chart.addSeries(LineSeries, {
        color: '#2962FF', lineWidth: 1,
        crosshairMarkerVisible: false, priceLineVisible: false, visible: visibleEmas[240],
        priceFormat: {
          type: 'price',
          precision: precision,
          minMove: minMove,
        },
      });

      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: '#26a69a',
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
        visible: visibleVolume,
      });

      chart.priceScale('volume').applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
      });

      const rsiSeries = chart.addSeries(LineSeries, {
        color: isDark ? '#ab47bc' : '#7b1fa2',
        lineWidth: 1,
        priceScaleId: 'rsi',
        visible: visibleRsi,
        priceFormat: {
          type: 'price',
          precision: 2,
          minMove: 0.01,
        },
      });

      chart.priceScale('rsi').applyOptions({
        scaleMargins: {
          top: 0.75,
          bottom: 0.05,
        },
        borderVisible: false,
      });

      rsiSeries.createPriceLine({
        price: 80, color: 'rgba(239, 83, 80, 0.3)', lineWidth: 1,
        lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '80',
      });
      rsiSeries.createPriceLine({
        price: 70, color: 'rgba(239, 83, 80, 0.4)', lineWidth: 1,
        lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '70',
      });
      rsiSeries.createPriceLine({
        price: 50, color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)', lineWidth: 1,
        lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '50',
      });
      rsiSeries.createPriceLine({
        price: 30, color: 'rgba(38, 166, 154, 0.4)', lineWidth: 1,
        lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '30',
      });
      rsiSeries.createPriceLine({
        price: 20, color: 'rgba(38, 166, 154, 0.3)', lineWidth: 1,
        lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '20',
      });

      const bbOptions: any = {
        color: isDark ? 'rgba(211, 211, 211, 0.4)' : 'rgba(128, 128, 128, 0.4)',
        lineWidth: 1,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        visible: visibleBb,
      };

      const bbUpperSeries = chart.addSeries(LineSeries, { ...bbOptions });
      const bbMiddleSeries = chart.addSeries(LineSeries, { ...bbOptions, lineStyle: LineStyle.Dashed });
      const bbLowerSeries = chart.addSeries(LineSeries, { ...bbOptions });

      candlestickSeriesRef.current = candlestickSeries;
      ema7SeriesRef.current = ema7Series;
      ema25SeriesRef.current = ema25Series;
      ema60SeriesRef.current = ema60Series;
      ema120SeriesRef.current = ema120Series;
      ema240SeriesRef.current = ema240Series;
      volumeSeriesRef.current = volumeSeries;
      rsiSeriesRef.current = rsiSeries;
      bbUpperSeriesRef.current = bbUpperSeries;
      bbMiddleSeriesRef.current = bbMiddleSeries;
      bbLowerSeriesRef.current = bbLowerSeries;
      chartRef.current = chart;

      // 십자선 이동 시 가격 업데이트
      chart.subscribeCrosshairMove((param) => {
        if (param.point && candlestickSeriesRef.current) {
          const data = param.seriesData.get(candlestickSeriesRef.current);
          const priceAtCursor = candlestickSeriesRef.current.coordinateToPrice(param.point.y);

          // 현재가(가장 최근 데이터의 종가) 가져오기
          let lastPrice = 0;
          try {
            const allData = candlestickSeriesRef.current.data();
            if (allData && allData.length > 0) {
              lastPrice = allData[allData.length - 1].close;
            }
          } catch (e) { }

          setLegendText(data, priceAtCursor, lastPrice, param.time);
        } else {
          setLegendText(undefined);
        }
      });

      const handleResize = () => {
        if (chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };

      window.addEventListener('resize', handleResize);
      fetchData(true); // 첫 생성 시에만 Initial Loading 표시

      return () => {
        window.removeEventListener('resize', handleResize);
        chart.remove();
        if (legend.parentNode) legend.parentNode.removeChild(legend);
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        ema7SeriesRef.current = null;
        ema25SeriesRef.current = null;
        ema60SeriesRef.current = null;
        ema120SeriesRef.current = null;
        ema240SeriesRef.current = null;
        volumeSeriesRef.current = null;
        rsiSeriesRef.current = null;
        bbUpperSeriesRef.current = null;
        bbMiddleSeriesRef.current = null;
        bbLowerSeriesRef.current = null;
      };
    }
  }, [isOpen, symbol, exchange]);

  // 주기적 데이터 갱신
  useEffect(() => {
    if (isOpen) {
      // timeframe이 바뀔 때만 즉시 갱신 (symbol/exchange 변경은 생성 효과에서 처리됨)
      if (candlestickSeriesRef.current && !isInitialLoading) {
        fetchData(false);
      }

      const timer = setInterval(() => {
        fetchData(false); // 배경 업데이트는 isLoading만 체크 (UI 로드는 안띄움)
      }, 15000);
      return () => clearInterval(timer);
    }
  }, [isOpen, timeframe, symbol, fetchData]);

  useEffect(() => {
    if (ema7SeriesRef.current) ema7SeriesRef.current.applyOptions({ visible: visibleEmas[7] });
    if (ema25SeriesRef.current) ema25SeriesRef.current.applyOptions({ visible: visibleEmas[25] });
    if (ema60SeriesRef.current) ema60SeriesRef.current.applyOptions({ visible: visibleEmas[60] });
    if (ema120SeriesRef.current) ema120SeriesRef.current.applyOptions({ visible: visibleEmas[120] });
    if (ema240SeriesRef.current) ema240SeriesRef.current.applyOptions({ visible: visibleEmas[240] });
  }, [visibleEmas]);

  useEffect(() => {
    if (volumeSeriesRef.current) volumeSeriesRef.current.applyOptions({ visible: visibleVolume });
  }, [visibleVolume]);

  useEffect(() => {
    if (rsiSeriesRef.current) rsiSeriesRef.current.applyOptions({ visible: visibleRsi });
  }, [visibleRsi]);

  useEffect(() => {
    const options = { visible: visibleBb };
    if (bbUpperSeriesRef.current) bbUpperSeriesRef.current.applyOptions(options);
    if (bbMiddleSeriesRef.current) bbMiddleSeriesRef.current.applyOptions(options);
    if (bbLowerSeriesRef.current) bbLowerSeriesRef.current.applyOptions(options);
  }, [visibleBb]);

  // 가격 라인 업데이트 최적화: 내용이 변했을 때만 갱신
  const prevPositionsRef = useRef<string>('');
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;

    const positionsStr = JSON.stringify({ positions, visiblePositions });
    if (prevPositionsRef.current === positionsStr) return;
    prevPositionsRef.current = positionsStr;

    // 기존 가격 라인 제거
    priceLinesRef.current.forEach(line => candlestickSeriesRef.current.removePriceLine(line));
    priceLinesRef.current = [];

    // 새로운 가격 라인 추가
    if (positions && positions.length > 0) {
      positions.forEach(pos => {
        const avgPrice = Number(pos.avg_price);
        const lineSide = (pos.side || 'long').toLowerCase();

        if (avgPrice > 0 && visiblePositions[lineSide]) {
          const lineColor = lineSide === 'long' ? '#22c55e' : '#ef4444';

          const priceLine = candlestickSeriesRef.current.createPriceLine({
            price: avgPrice, color: lineColor, lineWidth: 2,
            lineStyle: LineStyle.Dashed, axisLabelVisible: true,
          });
          priceLinesRef.current.push(priceLine);
        }
      });
    }
  }, [positions, visiblePositions, symbol]);

  // 테마 동기화
  useEffect(() => {
    if (chartRef.current) {
      const isDark = theme === 'dark' || (theme === 'default' && document.documentElement.classList.contains('dark'));

      chartRef.current.applyOptions({
        layout: { textColor: isDark ? '#D1D4DC' : '#131722' },
        grid: {
          vertLines: { color: isDark ? 'rgba(70, 70, 70, 0.2)' : 'rgba(230, 230, 230, 0.5)' },
          horzLines: { color: isDark ? 'rgba(70, 70, 70, 0.2)' : 'rgba(230, 230, 230, 0.5)' },
        },
      });

      if (ema7SeriesRef.current) {
        ema7SeriesRef.current.applyOptions({ color: isDark ? '#E1E1E1' : '#000000' });
      }
    }
  }, [theme]);

  const isDarkCurrent = theme === 'dark' || (theme === 'default' && typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  return (
    <Card className="rounded-3xl border border-border shadow-sm bg-card overflow-hidden mt-4">
      {/* 컴팩트해진 1줄 헤더 */}
      <div className="flex items-center justify-between p-2 px-4 border-b border-border bg-muted/[0.01]">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-xs font-black text-foreground">
            {symbol} 차트
          </h3>
          {isTestnet && (
            <div className="flex items-center gap-1">
              <span className={`text-[8px] font-bold px-1 py-0.5 rounded leading-none ${useTestnetData ? 'bg-indigo-500/20 text-indigo-500' : 'bg-muted text-muted-foreground'}`}>
                {useTestnetData ? 'Testnet' : 'Mainnet'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUseTestnetData(!useTestnetData)}
                className={`h-5 px-1.5 text-[8px] rounded transition-all leading-none ${useTestnetData
                  ? "text-red-600 dark:text-red-400 font-black border-red-500/40"
                  : "text-muted-foreground font-bold border-border"
                  }`}
              >
                use testnet
              </Button>
            </div>
          )}
        </div>

        {/* 컴팩트 우측 컨트롤 */}
        <div className="flex items-center gap-1">
          {isOpen && (
            <Button
              variant="ghost" size="sm" onClick={handleRefresh}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="차트 최적화"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
          <Button
            variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            title={isOpen ? "축소" : "펼치기"}
          >
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <CardContent className="p-0 relative">
          {/* 타임프레임 및 보조지표 영역 - 차트 본문 최상단 배치 */}
          <div className="p-3 border-b border-border bg-muted/5 flex flex-col gap-2">
            <div className="flex gap-1 bg-muted/30 p-1 rounded-lg w-fit">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => setTimeframe(tf.value)}
                  className={`px-2 py-1 text-[10px] font-bold rounded-md transition-colors ${timeframe === tf.value ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted text-muted-foreground'
                    }`}
                >
                  {tf.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-bold text-muted-foreground px-1">
              <button
                onClick={() => setVisibleEmas(prev => ({ ...prev, 7: !prev[7] }))}
                className={`flex items-center gap-1 transition-opacity ${visibleEmas[7] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${isDarkCurrent ? 'bg-white' : 'bg-black'}`} /> EMA 7
              </button>
              <button
                onClick={() => setVisibleEmas(prev => ({ ...prev, 25: !prev[25] }))}
                className={`flex items-center gap-1 transition-opacity ${visibleEmas[25] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#f82f2b]" /> EMA 25
              </button>
              <button
                onClick={() => setVisibleEmas(prev => ({ ...prev, 60: !prev[60] }))}
                className={`flex items-center gap-1 transition-opacity ${visibleEmas[60] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#fda635]" /> EMA 60
              </button>
              <button
                onClick={() => setVisibleEmas(prev => ({ ...prev, 120: !prev[120] }))}
                className={`flex items-center gap-1 transition-opacity ${visibleEmas[120] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" /> EMA 120
              </button>
              <button
                onClick={() => setVisibleEmas(prev => ({ ...prev, 240: !prev[240] }))}
                className={`flex items-center gap-1 transition-opacity ${visibleEmas[240] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#2962FF]" /> EMA 240
              </button>
              <button
                onClick={() => setVisiblePositions(prev => ({ ...prev, long: !prev.long }))}
                className={`flex items-center gap-1 transition-opacity ${visiblePositions['long'] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" /> L
              </button>
              <button
                onClick={() => setVisiblePositions(prev => ({ ...prev, short: !prev.short }))}
                className={`flex items-center gap-1 transition-opacity ${visiblePositions['short'] ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#ef4444]" /> S
              </button>
              <div className="w-px h-2 bg-border mx-1" />
              <button
                onClick={() => setVisibleVolume(!visibleVolume)}
                className={`flex items-center gap-1 transition-opacity ${visibleVolume ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#26a69a]" /> Vol
              </button>
              <button
                onClick={() => setVisibleRsi(!visibleRsi)}
                className={`flex items-center gap-1 transition-opacity ${visibleRsi ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#7b1fa2]" /> RSI
              </button>
              <button
                onClick={() => setVisibleBb(!visibleBb)}
                className={`flex items-center gap-1 transition-opacity ${visibleBb ? 'opacity-100' : 'opacity-30'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" /> BB
              </button>
            </div>
          </div>

          {isInitialLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <span className="text-[10px] font-black animate-pulse">데이터 로드 중...</span>
            </div>
          )}
          {!isOnline && (
            <div className="absolute top-2 right-2 bg-destructive/90 text-destructive-foreground px-2 py-0.5 rounded-full text-[9px] font-bold z-20 flex items-center gap-1 shadow-sm">
              <WifiOff className="w-2.5 h-2.5" /> 통신 끊김
            </div>
          )}
          <div ref={chartContainerRef} className="w-full relative" />
          <div className="p-2 border-t border-border flex justify-center bg-muted/5">
            <Button
              variant="ghost" size="sm" onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-foreground h-8 w-full text-[11px] font-bold"
            >
              <ChevronUp className="w-3 h-3 mr-1" /> 차트 접기
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
});
